import { useEffect, useState } from 'react';
import { ApiError, call } from '../api';
import { Avatar } from '../components/Avatar';
import { LevelRing, Medal, TIER_LABEL, type Cabinet, type Tier, type Trophy } from '../components/Trophies';
import { useApp } from '../context';
import { linkProps } from '../router';

/**
 * Profiel: je trophykast (level, uitgelichte trophies, recent verdiend, alles per categorie) en je vrienden.
 * /profile, /profile/friends en /profile/friend/:id (de kast van een vriend).
 */

const errText = (e: unknown, fallback: string) => (e instanceof ApiError && e.message !== e.code ? e.message : fallback);
const dateNl = (ms: number) => new Date(ms).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
const TIERS: Tier[] = ['platinum', 'gold', 'silver', 'bronze'];

export function Profile({ tab, friend }: { tab: 'trophies' | 'friends'; friend: string | null }) {
  const { me } = useApp();
  const profile = me.profile!;
  return (
    <main className="page profile-page">
      {!friend && (
        <nav className="filterbar profile-tabs" aria-label="Profiel" data-focus-group>
          <a className="chip" {...linkProps('/profile')} aria-current={tab === 'trophies' ? 'page' : undefined} aria-selected={tab === 'trophies'}>Trophies</a>
          <a className="chip" {...linkProps('/profile/friends')} aria-current={tab === 'friends' ? 'page' : undefined} aria-selected={tab === 'friends'}>Vrienden</a>
        </nav>
      )}
      {friend ? <TrophyCase key={friend} friend={friend} /> : tab === 'friends' ? <Friends /> : <TrophyCase friend={null} fallbackName={profile.title} fallbackThumb={profile.thumb} />}
    </main>
  );
}

function TrophyCase({ friend, fallbackName, fallbackThumb }: { friend: string | null; fallbackName?: string; fallbackThumb?: string }) {
  const [c, setC] = useState<Cabinet | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [pick, setPick] = useState<string[]>([]);
  const [open, setOpen] = useState<Trophy | null>(null);
  const mine = !friend;

  const load = (refresh = false) => {
    call<Cabinet>(friend ? `/api/friends/${encodeURIComponent(friend)}/trophies` : `/api/trophies${refresh ? '?refresh=1' : ''}`)
      .then((x) => { setC(x); setPick(x.showcase.map((t) => t.id)); })
      .catch((e: unknown) => setError(errText(e, 'De trophies konden niet geladen worden.')));
  };
  useEffect(() => load(), [friend]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <p className="placeholder-row error" role="alert">{error}</p>;
  if (!c) return <p className="subtle">Trophies tellen… (de eerste keer kan dit even duren)</p>;

  const toggle = (t: Trophy) => {
    if (!t.got) return;
    setPick((p) => (p.includes(t.id) ? p.filter((x) => x !== t.id) : p.length >= 3 ? [...p.slice(1), t.id] : [...p, t.id]));
  };
  const saveShowcase = () => {
    void call('/api/trophies/showcase', { method: 'PUT', body: JSON.stringify({ showcase: pick }) }).then(() => { setPicking(false); load(); });
  };
  const onTrophy = (t: Trophy) => (picking ? toggle(t) : setOpen(t));

  return (
    <>
      <header className="tc-head">
        <Avatar src={c.thumb || fallbackThumb} name={c.name || fallbackName || ''} className="tc-avatar" />
        <div className="tc-who">
          <h1>{c.name || fallbackName}</h1>
          <p className="subtle">{c.points.toLocaleString('nl-NL')} punten · {c.earned} van {c.total} trophies</p>
          <div className="tc-tiers">
            {TIERS.map((t) => (
              <span key={t} className={`tc-tier is-${t}`} title={TIER_LABEL[t]}>
                <Medal t={{ tier: t, cat: 'secret', got: 1, secret: false }} size={22} />
                <b>{c.counts[t]}</b>
              </span>
            ))}
          </div>
        </div>
        <LevelRing level={c.level} pct={c.levelPct} />
      </header>

      <section className="tc-section">
        <div className="tc-section-head">
          <h2>Uitgelicht</h2>
          {mine && !picking && <button className="btn btn-sm" onClick={() => setPicking(true)}>Kiezen</button>}
          {mine && picking && (
            <span className="form-actions">
              <span className="subtle">Kies er drie hieronder ({pick.length}/3)</span>
              <button className="btn btn-sm" onClick={() => { setPicking(false); setPick(c.showcase.map((t) => t.id)); }}>Annuleren</button>
              <button className="btn btn-sm btn-primary" onClick={saveShowcase}>Bewaren</button>
            </span>
          )}
        </div>
        <div className="tc-showcase">
          {[0, 1, 2].map((i) => {
            const t = picking ? c.trophies.find((x) => x.id === pick[i]) : c.showcase[i];
            return t ? (
              <button key={i} className="tc-show" onClick={() => setOpen(t)}>
                <Medal t={t} size={84} />
                <b>{t.name}</b>
                <span className="subtle">{TIER_LABEL[t.tier]}</span>
              </button>
            ) : (
              <div key={i} className="tc-show is-empty"><span className="subtle">{mine ? 'Nog niets gekozen' : '—'}</span></div>
            );
          })}
        </div>
      </section>

      {c.recent.length > 0 && (
        <section className="tc-section">
          <h2>Recent verdiend</h2>
          <div className="tc-recent">
            {c.recent.map((t) => (
              <button key={t.id} className="tc-recent-item" onClick={() => onTrophy(t)}>
                <Medal t={t} size={44} />
                <span><b>{t.name}</b><span className="subtle">{t.got ? dateNl(t.got) : ''}</span></span>
              </button>
            ))}
          </div>
        </section>
      )}

      {c.categories.map((cat) => {
        const list = c.trophies.filter((t) => t.cat === cat.id);
        const got = list.filter((t) => t.got).length;
        return (
          <section key={cat.id} className="tc-section">
            <div className="tc-section-head">
              <h2>{cat.name}</h2>
              <span className="subtle">{got} / {list.length}</span>
            </div>
            <div className="tc-grid">
              {list.map((t) => (
                <button key={t.id} className={`tc-card${t.got ? '' : ' is-locked'}${picking && pick.includes(t.id) ? ' is-picked' : ''}`} onClick={() => onTrophy(t)} disabled={picking && !t.got}>
                  <Medal t={t} size={56} />
                  <span className="tc-card-body">
                    <b>{t.name}</b>
                    <span className="subtle">{t.text}</span>
                    {!t.got && t.goal !== null && t.progress !== null && (
                      <span className="tc-bar" aria-label={`${t.progress} van ${t.goal}`}><i style={{ width: `${(t.progress / t.goal) * 100}%` }} /><em>{fmt(t.progress)} / {fmt(t.goal)}</em></span>
                    )}
                    <span className="tc-meta">
                      <span className={`tc-pill is-${t.tier}`}>{TIER_LABEL[t.tier]}</span>
                      {t.rarity !== null && <span className="subtle">{t.rarity}% heeft deze</span>}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </section>
        );
      })}

      <p className="subtle tc-foot">
        {c.computedAt ? `Bijgewerkt ${new Date(c.computedAt).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}. ` : ''}
        Telt mee: wat je in Plex keek, via addons keek, live sport en muziek. Geheime trophies zie je pas als je ze hebt.
        {mine && <> <button className="btn btn-sm tc-refresh" onClick={() => { setC(null); load(true); }}>Nu bijwerken</button></>}
      </p>

      {open && (
        <div className="swipe-match" role="dialog" aria-modal="true" aria-label={open.name} onClick={() => setOpen(null)}>
          <div className="swipe-match-body tc-detail" onClick={(e) => e.stopPropagation()}>
            <Medal t={open} size={140} />
            <h2>{open.name}</h2>
            <p>{open.text}</p>
            <p className="subtle">
              {TIER_LABEL[open.tier]} · {({ bronze: 15, silver: 30, gold: 90, platinum: 300 } as Record<Tier, number>)[open.tier]} punten
              {open.rarity !== null ? ` · ${open.rarity}% heeft deze` : ''}
            </p>
            <p className="subtle">{open.got ? `Verdiend op ${dateNl(open.got)}` : open.goal !== null && open.progress !== null ? `Nog ${fmt(open.goal - open.progress)} te gaan` : 'Nog niet verdiend'}</p>
            <div className="form-actions"><button className="btn" data-autofocus onClick={() => setOpen(null)}>Sluiten</button></div>
          </div>
        </div>
      )}
    </>
  );
}

const fmt = (n: number) => (Number.isInteger(n) ? n.toLocaleString('nl-NL') : n.toFixed(1).replace('.', ','));

interface Person { id: string; name: string; thumb: string }
interface FriendsData {
  code: string;
  me: Person & { points: number; level: number; earned: number };
  friends: (Person & { since: number; points: number; level: number; earned: number })[];
  incoming: (Person & { at: number })[];
  outgoing: (Person & { at: number })[];
}

function Friends() {
  const [d, setD] = useState<FriendsData | null>(null);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  const load = () => { call<FriendsData>('/api/friends').then(setD).catch(() => setMsg({ ok: false, text: 'Vrienden konden niet geladen worden.' })); };
  useEffect(load, []);

  const act = (path: string, method = 'POST') => { void call(path, { method }).then(load).catch(() => undefined); };
  const invite = () => {
    setMsg(null);
    call<{ friends: boolean; name: string }>('/api/friends/invite', { method: 'POST', body: JSON.stringify({ code }) })
      .then((r) => { setCode(''); setMsg({ ok: true, text: r.friends ? `Jij en ${r.name} zijn nu vrienden.` : `Uitnodiging gestuurd naar ${r.name}.` }); load(); })
      .catch((e: unknown) => setMsg({ ok: false, text: errText(e, 'Uitnodigen lukte niet.') }));
  };
  const copy = () => {
    if (!d) return;
    void navigator.clipboard?.writeText(d.code).then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 2000); }).catch(() => undefined);
  };
  const newCode = () => { void call<{ code: string }>('/api/friends/code', { method: 'POST' }).then(load); };

  if (!d) return msg ? <p className="placeholder-row error" role="alert">{msg.text}</p> : <p className="subtle">Laden…</p>;
  const board = [...d.friends.map((f) => ({ ...f, me: false })), { ...d.me, since: 0, me: true }].sort((a, b) => b.points - a.points);

  return (
    <>
      <div className="fr-top">
        <section className="swipe-box">
          <h2>Jouw vriendencode</h2>
          <p className="fr-code" aria-label={`Code ${d.code}`}>{d.code}</p>
          <p className="subtle">Geef deze code aan iemand op deze server. Die kan je dan uitnodigen.</p>
          <div className="form-actions">
            <button className="btn btn-sm" onClick={copy}>{copied ? 'Gekopieerd' : 'Kopiëren'}</button>
            <button className="btn btn-sm" onClick={newCode} title="De oude code werkt dan niet meer; je vrienden blijven">Nieuwe code</button>
          </div>
        </section>
        <section className="swipe-box">
          <h2>Iemand uitnodigen</h2>
          <form className="swipe-join" onSubmit={(e) => { e.preventDefault(); if (code.replace(/[^a-z0-9]/gi, '').length === 6) invite(); }}>
            <input className="input fr-input" value={code} maxLength={7} placeholder="ABC-123" aria-label="Vriendencode" autoCapitalize="characters" onChange={(e) => setCode(e.target.value.toUpperCase())} />
            <button className="btn btn-primary" type="submit" disabled={code.replace(/[^a-z0-9]/gi, '').length !== 6}>Uitnodigen</button>
          </form>
          {msg && <p className={msg.ok ? 'subtle' : 'error'} role="status">{msg.text}</p>}
        </section>
      </div>

      {d.incoming.length > 0 && (
        <section className="tc-section">
          <h2>Uitnodigingen</h2>
          <ul className="fr-list">
            {d.incoming.map((p) => (
              <li key={p.id}>
                <Avatar src={p.thumb} name={p.name} className="fr-avatar" />
                <span className="fr-name"><b>{p.name}</b><span className="subtle">wil vrienden worden</span></span>
                <span className="form-actions">
                  <button className="btn btn-sm btn-primary" onClick={() => act(`/api/friends/${encodeURIComponent(p.id)}/accept`)}>Accepteren</button>
                  <button className="btn btn-sm" onClick={() => act(`/api/friends/${encodeURIComponent(p.id)}/decline`)}>Weigeren</button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="tc-section">
        <h2>Ranglijst</h2>
        {d.friends.length === 0 && <p className="subtle">Nog geen vrienden. Nodig iemand uit met zijn of haar code; daarna kunnen jullie elkaars trophies zien.</p>}
        <ol className="fr-list fr-board">
          {board.map((p, i) => (
            <li key={p.id} className={p.me ? 'is-me' : ''}>
              <span className="fr-rank">{i + 1}</span>
              <Avatar src={p.thumb} name={p.name} className="fr-avatar" />
              <span className="fr-name"><b>{p.name}{p.me ? ' (jij)' : ''}</b><span className="subtle">Level {p.level} · {p.earned} trophies</span></span>
              <b className="fr-points">{p.points.toLocaleString('nl-NL')}</b>
              {p.me ? (
                <a className="btn btn-sm" {...linkProps('/profile')}>Bekijken</a>
              ) : (
                <span className="form-actions">
                  <a className="btn btn-sm" {...linkProps(`/profile/friend/${encodeURIComponent(p.id)}`)}>Bekijken</a>
                  <button className="btn btn-sm" onClick={() => { if (removing === p.id) { setRemoving(null); act(`/api/friends/${encodeURIComponent(p.id)}`, 'DELETE'); } else setRemoving(p.id); }}>{removing === p.id ? 'Zeker weten?' : 'Verwijderen'}</button>
                </span>
              )}
            </li>
          ))}
        </ol>
      </section>

      {d.outgoing.length > 0 && (
        <section className="tc-section">
          <h2>Verstuurd</h2>
          <ul className="fr-list">
            {d.outgoing.map((p) => (
              <li key={p.id}>
                <Avatar src={p.thumb} name={p.name} className="fr-avatar" />
                <span className="fr-name"><b>{p.name}</b><span className="subtle">wacht op antwoord</span></span>
                <button className="btn btn-sm" onClick={() => act(`/api/friends/${encodeURIComponent(p.id)}/decline`)}>Intrekken</button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
